import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { firmFromQS } from "@/lib/nav";
import { useUrlState } from "@/lib/useUrlState";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { TierBadge, PocChip, PocChipPair, RiskBadge, UsersCell } from "@/components/Badges";
import { FirmSubmeta } from "@/components/FirmSubmeta";
import { RISK_META, RISK_FALLBACK } from "@/lib/risk";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { CalendarHeart, Clock, CaretRight, Phone, Hourglass, CheckCircle, XCircle, MagnifyingGlass, X } from "@phosphor-icons/react";
import ExportButton from "@/components/ExportButton";
import ChurnDialog from "@/components/ChurnDialog";
import { RENEWAL_COLUMNS } from "@/lib/exportColumns";
import FirmRowActions from "@/components/FirmRowActions";

const BUCKETS = [
  { key: "30", label: "Next 30 days", subtitle: "Closest to renewal — call this week", color: "red" },
  { key: "60", label: "31 – 60 days", subtitle: "Schedule renewal conversations", color: "amber" },
  { key: "90", label: "61 – 90 days", subtitle: "Heads-up window", color: "blue" },
];

const COLOR_MAP = {
  red: { dot: "bg-red-500", bg: "bg-red-50", text: "text-red-700", border: "border-red-200", ring: "ring-red-100", bar: "bg-red-500" },
  amber: { dot: "bg-amber-500", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", ring: "ring-amber-100", bar: "bg-amber-500" },
  blue: { dot: "bg-blue-500", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", ring: "ring-blue-100", bar: "bg-blue-500" },
};

export default function Renewals() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isPoc = user?.role === "poc";
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useUrlState("bucket", "30");
  const [loading, setLoading] = useState(true);

  // ----- Filter bar -----
  const [search, setSearch] = useUrlState("search", "");
  const [tier, setTier] = useUrlState("tier", "all");
  const [poc, setPoc] = useUrlState("poc", "all");
  const [pocOptions, setPocOptions] = useState([]);
  const [tierOptions, setTierOptions] = useState([]);

  // ----- Lifted Pending Decision data so filters apply uniformly -----
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const loadPending = () => {
    setPendingLoading(true);
    api
      .get("/firms/renewal-pending")
      .then((r) => setPendingRows(r.data.firms || []))
      .finally(() => setPendingLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    api
      .get("/customers/renewals")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    loadPending();
    api.get("/meta/options").then((r) => {
      setPocOptions(r.data.pocs || []);
      setTierOptions(r.data.tiers || []);
    }).catch(() => {});
  }, []);

  // Single predicate reused across pending list AND each bucket so the screen
  // can never drift between "what the filter bar shows" and "what the counts say".
  const matchesFilters = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (f) => {
      if (tier !== "all" && f.tier !== tier) return false;
      if (poc !== "all" && f.poc !== poc) return false;
      if (q) {
        const hay = [
          f.firm_name,
          f.subdomain,
          f.firm_url,
          f.city,
          f.poc,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    };
  }, [search, tier, poc]);

  // Filtered slices — counts shown in the summary cards always come from THESE.
  const filteredBuckets = useMemo(() => {
    const src = data?.buckets || {};
    return {
      "30": (src["30"] || []).filter(matchesFilters),
      "60": (src["60"] || []).filter(matchesFilters),
      "90": (src["90"] || []).filter(matchesFilters),
    };
  }, [data, matchesFilters]);

  const filteredPending = useMemo(
    () => pendingRows.filter(matchesFilters),
    [pendingRows, matchesFilters]
  );

  const filtersDirty = search || tier !== "all" || poc !== "all";
  const clearFilters = () => {
    setSearch("");
    setTier("all");
    setPoc("all");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900 flex items-center gap-2">
          <CalendarHeart size={28} weight="fill" className="text-violet-600" />
          Renewal Customers
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Firms whose contract renews soon{" "}
          {isPoc && <span className="font-mono text-violet-700">· scoped to POC {user.poc_name}</span>}. Uses the team-set subscription end date, falling back to activation + 1 year (shown with an <span className="text-amber-700">AUTO</span> badge).
        </p>
      </div>

      {/* Unified filter bar — applies to BOTH the Renewal Pending panel and the 3 bucket tables/cards */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-center gap-2" data-testid="renewals-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search firm, subdomain, city, POC…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
            data-testid="renewals-filter-search"
          />
        </div>
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="renewals-filter-tier">
            <SelectValue placeholder="All tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            {tierOptions.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={poc} onValueChange={setPoc}>
          <SelectTrigger className="w-[150px] h-9 text-sm" data-testid="renewals-filter-poc">
            <SelectValue placeholder="All POCs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All POCs</SelectItem>
            {pocOptions.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={activeTab} onValueChange={setActiveTab}>
          <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="renewals-filter-bucket">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BUCKETS.map((b) => (
              <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filtersDirty && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs text-gray-500" data-testid="renewals-filter-clear">
            <X size={12} className="mr-1" /> Clear
          </Button>
        )}
      </div>

      <PendingDecisionPanel
        isPoc={isPoc}
        rows={filteredPending}
        rawCount={pendingRows.length}
        loading={pendingLoading}
        onChange={loadPending}
      />

      {/* Summary cards — counts reflect ACTIVE filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="renewals-summary">
        {BUCKETS.map((b) => {
          const c = COLOR_MAP[b.color];
          const count = filteredBuckets[b.key]?.length ?? 0;
          const isActive = activeTab === b.key;
          return (
            <button
              key={b.key}
              onClick={() => setActiveTab(b.key)}
              className={`text-left bg-white border rounded-lg p-5 transition-all ${
                isActive
                  ? `${c.border} shadow-md ring-2 ${c.ring}`
                  : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`renewals-tab-${b.key}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                  <span className="text-[11px] uppercase tracking-wider font-medium text-gray-600">
                    {b.label}
                  </span>
                </div>
                {isActive && (
                  <span className={`text-[10px] uppercase tracking-wider font-bold ${c.text}`}>
                    Selected
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-[36px] leading-none font-semibold text-gray-900 tabular-nums">
                  {count}
                </span>
                <span className="text-xs text-gray-500">firm{count === 1 ? "" : "s"}</span>
              </div>
              <div className="text-[11px] text-gray-500 mt-2">{b.subtitle}</div>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="renewals-table">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="font-display text-sm font-semibold text-gray-900">
              {BUCKETS.find((b) => b.key === activeTab)?.label}
              <span className="ml-2 text-[11px] font-mono text-gray-500">
                {(filteredBuckets[activeTab] || []).length} firms
              </span>
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              Click a row to open the firm 360° view · Sorted by closest renewal first
            </div>
          </div>
          <ExportButton
            filename={`renewals-${activeTab}d`}
            sheetName="Renewals"
            columns={RENEWAL_COLUMNS}
            rows={filteredBuckets[activeTab] || []}
            testId={`export-renewals-${activeTab}`}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-left">
              <tr>
                <Th>Firm</Th>
                <Th>Users</Th>
                <Th>Tier</Th>
                <Th>Risk</Th>
                <Th>POC</Th>
                <Th className="text-right">Renewal date</Th>
                <Th className="text-right">In</Th>
                <th className="w-16"></th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-400">Loading…</td></tr>
              )}
              {!loading && (filteredBuckets[activeTab] || []).length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-400">
                  {filtersDirty
                    ? "No renewals match the current filters."
                    : "No renewals in this window. Healthy."}
                </td></tr>
              )}
              {!loading && (filteredBuckets[activeTab] || []).map((r) => {
                const meta = RISK_META[r.risk] || RISK_FALLBACK;
                return (
                <tr
                  key={r.subdomain}
                  onClick={() => navigate(`/firms/${r.subdomain}${firmFromQS(location)}`)}
                  className={`border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${meta.leftClass}`}
                  data-testid={`renewal-row-${r.subdomain}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{r.firm_name}</div>
                    <FirmSubmeta firm={r} />
                  </td>
                  <td className="px-4 py-3"><UsersCell value={r.num_users} /></td>
                  <td className="px-4 py-3"><TierBadge tier={r.tier} /></td>
                  <td className="px-4 py-3"><RiskBadge risk={r.risk} /></td>
                  <td className="px-4 py-3"><PocChipPair poc={r.poc} salesPoc={r.sales_poc} /></td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-gray-900">
                    <div className="flex items-center justify-end gap-2">
                      <span>{new Date(r.renewal_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                      {r.renewal_source === "auto" && (
                        <span
                          className="inline-flex items-center text-[9px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5"
                          title="No manual subscription end date set — computed as activation + 1 year"
                        >
                          auto
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-semibold ${urgencyClass(r.days_to_renewal)}`}>
                      <Clock size={11} />
                      {r.days_to_renewal}d
                    </span>
                  </td>
                  <td className="px-2 py-3 whitespace-nowrap"><FirmRowActions firm={r} /></td>
                  <td className="px-4 py-3 text-right"><CaretRight size={14} className="text-gray-300" /></td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400 flex items-center gap-2">
          <Phone size={11} />
          Tip: firms with a Potential Churn or Inactive risk badge are highest priority — call them before renewal date.
        </div>
      </div>
    </div>
  );
}

function urgencyClass(days) {
  if (days <= 7) return "bg-red-50 border border-red-200 text-red-700";
  if (days <= 30) return "bg-amber-50 border border-amber-200 text-amber-700";
  if (days <= 60) return "bg-blue-50 border border-blue-200 text-blue-700";
  return "bg-gray-50 border border-gray-200 text-gray-600";
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500 ${className}`}>{children}</th>;
}


// ---------------- Renewal Pending panel ----------------
function PendingDecisionPanel({ isPoc, rows, rawCount, loading, onChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(true);
  const [renewFirm, setRenewFirm] = useState(null);
  const [churnFirm, setChurnFirm] = useState(null);

  // Hide the entire panel only when the SOURCE list is empty (not just the filtered
  // slice) — so an empty filter result still shows the panel with a helpful note.
  if (!loading && rawCount === 0) return null;

  return (
    <div className="bg-white border border-amber-200 rounded-lg overflow-hidden" data-testid="renewal-pending-panel">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full px-4 py-3 border-b border-amber-100 flex items-center justify-between hover:bg-amber-50/60 transition-colors text-left"
      >
        <div className="flex items-center gap-2.5">
          <Hourglass size={18} weight="fill" className="text-amber-600" />
          <div>
            <div className="font-display text-sm font-semibold text-gray-900">
              Renewal Pending · Decision needed
              <span className="ml-2 text-[11px] font-mono text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                {rows.length}
                {rows.length !== rawCount && (
                  <span className="text-amber-500/70"> / {rawCount}</span>
                )}
              </span>
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {isPoc
                ? "Firms whose subscription expired — awaiting admin decision. Read-only here; you'll see them in your Dashboard as normal."
                : "Subscription expired but no decision yet. Mark each firm as Renewed (sets a new end date) or Churned (removes from live screens)."}
            </div>
          </div>
        </div>
        <CaretRight
          size={14}
          className={`text-gray-400 transition-transform ${collapsed ? "" : "rotate-90"}`}
        />
      </button>

      {!collapsed && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60">
              <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                <th className="text-left px-4 py-2 font-medium">Firm</th>
                <th className="text-left px-3 py-2 font-medium">Users</th>
                <th className="text-left px-3 py-2 font-medium">Tier</th>
                <th className="text-left px-3 py-2 font-medium">Risk</th>
                <th className="text-left px-3 py-2 font-medium">POC</th>
                <th className="text-left px-3 py-2 font-medium">Subscription ended on</th>
                <th className="px-3 py-2 w-16"></th>
                {!isPoc && <th className="text-right px-4 py-2 font-medium">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={isPoc ? 7 : 8} className="px-4 py-8 text-center text-gray-400 text-sm">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={isPoc ? 7 : 8} className="px-4 py-6 text-center text-gray-400 text-sm">
                  No pending firms match the current filters.
                </td></tr>
              )}
              {!loading && rows.map((f) => {
                const meta = RISK_META[f.risk] || RISK_FALLBACK;
                return (
                <tr
                  key={f.subdomain}
                  className={`hover:bg-amber-50/40 ${meta.leftClass}`}
                  data-testid={`pending-row-${f.subdomain}`}
                >
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => navigate(`/firms/${f.subdomain}${firmFromQS(location)}`)}
                      className="text-left text-gray-900 font-medium hover:text-violet-700"
                    >
                      {f.firm_name}
                    </button>
                    <FirmSubmeta firm={f} />
                  </td>
                  <td className="px-3 py-2.5"><UsersCell value={f.num_users} /></td>
                  <td className="px-3 py-2.5"><TierBadge tier={f.tier} /></td>
                  <td className="px-3 py-2.5"><RiskBadge risk={f.risk} /></td>
                  <td className="px-3 py-2.5"><PocChipPair poc={f.poc} salesPoc={f.sales_poc} /></td>
                  <td className="px-3 py-2.5 text-gray-700 tabular-nums">{f.subscription_end_date || "—"}</td>
                  <td className="px-2 py-2.5 whitespace-nowrap"><FirmRowActions firm={f} /></td>
                  {!isPoc && (
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setRenewFirm(f)}
                        data-testid={`pending-renew-${f.subdomain}`}
                        title="Mark renewed"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-emerald-600 hover:bg-emerald-50 transition-colors mr-1"
                      >
                        <CheckCircle size={16} weight="fill" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setChurnFirm(f)}
                        data-testid={`pending-churn-${f.subdomain}`}
                        title="Mark churned"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <XCircle size={16} weight="fill" />
                      </button>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <MarkRenewedDialog
        firm={renewFirm}
        onClose={() => setRenewFirm(null)}
        onDone={() => { setRenewFirm(null); onChange?.(); }}
      />
      <ChurnDialog
        firm={churnFirm}
        onClose={() => setChurnFirm(null)}
        onConfirmed={() => { setChurnFirm(null); onChange?.(); }}
      />
    </div>
  );
}

function MarkRenewedDialog({ firm, onClose, onDone }) {
  const defaultDate = useMemo(() => {
    const base = firm?.subscription_end_date;
    if (!base) {
      const t = new Date();
      t.setFullYear(t.getFullYear() + 1);
      return t.toISOString().slice(0, 10);
    }
    // base is ISO YYYY-MM-DD; add 1 year carefully (handles Feb 29 by clamping).
    const [y, m, d] = base.split("-").map(Number);
    const next = new Date(Date.UTC(y + 1, m - 1, d));
    return next.toISOString().slice(0, 10);
  }, [firm]);

  const [newEnd, setNewEnd] = useState(defaultDate);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setNewEnd(defaultDate); }, [defaultDate, firm]);

  if (!firm) return null;
  const today = new Date().toISOString().slice(0, 10);
  const valid = !!newEnd && newEnd > today;

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await api.post(`/firms/${firm.subdomain}/mark-renewed`, {
        new_subscription_end_date: newEnd,
      });
      toast.success(`${firm.firm_name} renewed · new end date ${newEnd}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark renewed failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="mark-renewed-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CheckCircle size={18} className="text-emerald-600" />
            Mark Renewed · {firm.firm_name}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-2 space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-800 leading-relaxed">
            This firm will go back to <span className="font-semibold">Live</span> with the new
            subscription end date below. It'll continue to appear everywhere as a regular customer.
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-700">
              New subscription end date <span className="text-red-500">*</span>
            </Label>
            <div className="mt-1.5">
              <Input
                type="date"
                value={newEnd}
                min={today}
                onChange={(e) => setNewEnd(e.target.value)}
                data-testid="renew-new-end"
              />
              <div className="text-[10px] text-gray-500 mt-1">
                Defaults to the previous end date + 1 year ({defaultDate}). Adjust if the renewal
                cycle is different.
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="renew-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={!valid || submitting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="renew-confirm"
          >
            {submitting ? "Saving…" : "Confirm renewal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

